import { Fragment, useLayoutEffect, useRef, useState, type ReactNode } from "react";
import type { LookView, StoryView } from "../../shared/story";
import { WhereYouAre } from "../noirmap";
import {
  Care, Decision, VoiceShift, DeleteStory, Kept, SceneBreak, Weaving, waiting, More, SoundToggle, ChapterEnd, DreamNow, HurryHint, frayOf, inkOf, Plates, PlatesLink, Returned, SkinPicker, Unwoven, ReadingRoom, holdToHurry, Writer, asciiCard, linkify, quietFor, tensionSpark, threadOrder, type ThemeProps,
} from "../common";
import { Margins } from "../v2/margins";
import { PenLine, Pull } from "../v2/pens";
import { LookActions } from "../v2/pocket";
import { Credit, TrustLinks } from "../trust";
import type { Shown, StoryUI } from "../useStory";
import "./phosphor.css";

/**
 * B · Phosphor: an amber terminal MUD. The log is the story; the side panel (desktop) or the inline expanders
 * (phone) are drawn from the world graph for free: an ASCII map, found objects as cards, tension, threads.
 */

const BANNER = String.raw`┌───────────────────────────┐
│  W  E  F  T  W  A  K  E   │
└───────────────────────────┘`;

type Panel = "map" | "items" | "threads";

export function PhosphorTheme({ s, home, skin, setSkin, goHome, openPlates, meta }: ThemeProps) {
  const bodyRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState<Panel | null>(null);
  const view = s.view;
  // The log scrolls in its own panel, so a story opens at its top, as the page does in the other themes (App's nav);
  // without this, a phone that scrolled down to tap an opening kept that offset and showed the runway, not the story.
  useLayoutEffect(() => { bodyRef.current?.scrollTo(0, 0); }, [s.id]);

  const lastTurn = s.live ?? s.turns.at(-1);
  const n = s.live?.n ?? view?.n ?? s.turns.at(-1)?.n ?? 0;
  const tension = s.live?.tension ?? view?.tension ?? null;
  const intent = lastTurn?.intent ?? null;

  return (
    <main className={view ? "sk-phosphor" : "sk-phosphor ph-solo"}>
      <header className="ph-head">
        <a className="ph-brand" href="/" aria-label={s.id ? "Your stories" : undefined} onClick={(e) => { e.preventDefault(); if (s.id) meta("stories"); else goHome(); }}>WEFTWAKE{s.id && <span className="lm-brand-caret" aria-hidden="true"> ▾</span>}</a>
        <span className="ph-where">{view ? view.title.toLowerCase() : home ? "boot" : "…"}</span>
        {view && view.turns.length > 0 && (
          <span className="ph-spark" aria-label={`Tension ${tension?.toFixed(1)} of 5`}>
            {tensionSpark(view.turns.slice(-12))}<b>T{Math.round(tension ?? 3)}</b>
          </span>
        )}
        <SoundToggle className="ph-skins-sound" />
            <SkinPicker skin={skin} setSkin={setSkin} className="ph-skins" />
      </header>

      {view && (
        <nav className="ph-toggles" aria-label="World">
          {(["map", "threads"] as Panel[]).map((p) => (
            <button key={p} type="button" aria-expanded={open === p} onClick={() => setOpen(open === p ? null : p)}>
              [{open === p ? "−" : "+"}] {p === "map" ? "here" : p}
            </button>
          ))}
        </nav>
      )}
      {view && open && <div className="ph-inline">{panel(open, view, n, s)}</div>}

      <div className="ph-body" ref={bodyRef}>
        <section className="ph-log" role="log" aria-label="The story" aria-live="polite" aria-busy={!!s.live?.streaming || !!s.pending}>
          {home && <Boot s={s} home={home} />}
          {s.loadFailed && (
            <p className="ph-sys">! couldn't open this story. <button type="button" className="ph-link" onClick={s.reload}>retry</button></p>
          )}
          {view?.recap && s.turns[0] && s.turns[0].n > 1 && <p className="ph-sys"># previously: {view.recap}</p>}
          {s.turns.map((t) => (
            <Fragment key={t.n}>
              <Exchange s={s} t={t} things={view?.things ?? []} looks={s.looks[t.n]} onLook={(name) => s.look(t.n, name)} />
              <Plates s={s} at={t.n} className="ph-plate" />
            </Fragment>
          ))}
          {s.live && (
            <article className="ph-turn" data-turn={s.live.n}>
              <p className="ph-you"><span aria-hidden="true">&gt; </span><PenLine input={s.live.input} /></p>
              <Returned t={s.live} className="ph-sys" label={(name) => `# returns: ${name}`} />
              <p className={s.live.intent === "meta" ? "ph-sys" : "ph-prose"} data-prose={s.live.intent === "meta" ? undefined : s.live.n} data-live {...holdToHurry(s)}>
                {s.live.intent === "meta" && "# narrator: "}
                {s.live.text}
                {s.live.streaming && <span className="ph-cursor" aria-hidden="true" />}
                {s.live.paused && <span className="ph-paused"> [paused — typing]</span>}
              </p>
              {waiting(s) && <Weaving label="weaving…" className="ph-weaving" />}
              <HurryHint s={s} className="ph-sys" />
            </article>
          )}
          {s.pending && !s.live && (
            <article className="ph-turn" data-pending>
              <p className="ph-you"><span aria-hidden="true">&gt; </span>{s.pending}</p>
              <Weaving label="weaving…" className="ph-weaving" />
            </article>
          )}
          {s.notice && <p className="ph-sys ph-err" role="status">! {s.notice}</p>}
          <Care s={s} className="ph-care" />
          {/* The room the reply writes into (common.tsx ReadingRoom): nothing on screen moves while it's written. */}
          {s.id && <ReadingRoom s={s} writer=".ph-prompt" />}
        </section>

        {view && (
          <aside className="ph-panel" aria-label="The world">
            {panel("map", view, n, s)}
            {panel("threads", view, n, s)}
            <section>
              <h2>tension · last {view.turns.length} turns</h2>
              <pre className="ph-wave">{tensionSpark(view.turns) || "—"}</pre>
              <p className="ph-dim">now {tension?.toFixed(1) ?? "?"} / 5</p>
            </section>
          </aside>
        )}
      </div>

      {!s.resources && (
        <Writer
          s={s}
          className="ph-prompt"
          prompt={<span className="ph-caret" aria-hidden="true">&gt;</span>}
          sendLabel="⏎"
          placeholder={s.live?.streaming ? "type to pause · enter cuts in" : s.id ? "what do you do?" : "begin your story"}
        />
      )}

      <footer className="ph-status">
        <span className="ph-st-title">weftwake{view ? ` · ${view.title.toLowerCase()}` : ""}</span>
        {view && <span>turn {n}<span className="ph-dev"> · t {tension?.toFixed(1) ?? "?"} · {intent ?? "—"}</span></span>}
        <span className="ph-actions">
          <PlatesLink open={openPlates} label="ls plates/" />
          {s.id && <button type="button" onClick={() => meta("stories")}>ls stories/</button>}
          {s.id && <button type="button" onClick={() => meta("map")}>less journal</button>}
          {s.id && <button type="button" onClick={() => meta("cloth")}>cat cloth</button>}
          <button type="button" onClick={() => meta("feedback")}>mail feedback</button>
          <More label="more…">
            <TrustLinks meta={meta} />
            <a href="/api/export">export</a>
            {s.id && <DreamNow s={s} label="dream --now" />}
            {s.id && <button type="button" onClick={s.remove}>rm story</button>}
          </More>
          <span className="ph-dim">{s.live?.streaming ? "writing…" : "saved"}</span>
        </span>
      </footer>
    </main>
  );
}

function Boot({ s, home }: { s: StoryUI; home: NonNullable<ThemeProps["home"]> }) {
  return (
    <div className="ph-boot">
      <pre className="ph-banner" aria-label="Weftwake">{BANNER}</pre>
      <p className="ph-sys"># a story that isn't written yet. write a line or a page; the world grows from what you write.</p>
      <p className="ph-sys lm-what"># weftwake: a text adventure that writes itself. you write what you do; an AI narrator writes what happens. first 40 turns free.</p>
      <p className="ph-sys"># typing while the narrator writes pauses it. enter cuts in.</p>
      <ol className="ph-seeds">
        {home.seeds.map((o, i) => (
          <li key={o}><button type="button" onClick={() => s.send(o)} disabled={s.busy}>[{i + 1}] {o}</button></li>
        ))}
      </ol>
      <Credit className="ph-sys" />
      {home.stories.length > 0 && (
        <div className="ph-ls">
          <p className="ph-you"><span aria-hidden="true">&gt; </span>ls stories/</p>
          <ul>
            {home.stories.map((st) => (
              <li key={st.id}>
                <button type="button" className="ph-open" onClick={() => home.open(st.id)}>
                  <span>{st.title}</span><span className="ph-dim">{String(st.n).padStart(3, " ")} {st.n === 1 ? "turn" : "turns"}</span>
                </button>
                <DeleteStory title={st.title} remove={() => home.remove(st.id)} />
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function Exchange({ s, t, things, looks, onLook }: { s: StoryUI; t: Shown; things: StoryView["things"]; looks?: LookView[]; onLook(name: string): void }) {
  const meta = t.intent === "meta";
  return (
    <article className="ph-turn" data-turn={t.n}>
      <SceneBreak t={t} className="ph-scene" mark="--- scene ---" />
      <p className="ph-you"><span aria-hidden="true">&gt; </span><PenLine input={t.input} /><Kept t={t} className="ph-kept" /></p>
      <Returned t={t} className="ph-sys" label={(name) => `# returns: ${name}`} />
      <VoiceShift t={t} className="ph-sys" label={(name) => `# voice: ${name.toLowerCase()}`} />
      <Decision t={t} className="ph-decision" label="# the moment of decision" />
      {meta ? (
        <p className="ph-sys"># narrator: {t.prose}</p>
      ) : (
        <p className="ph-prose" data-prose={t.n}>
          {linkify(t.prose, things, onLook, "lm-thing ph-thing", inkOf(s.view, t.n))}
          {t.status === "cut" && <span className="ph-ctrlc" title="you cut in here"> ^C</span>}
          <Unwoven s={s} t={t} className="ph-dropped" label={(n) => ` [${n} words dropped]`} />
        </p>
      )}
      {!meta && <Pull s={s} n={t.n} className="ph-pull" />}
      {!meta && <Margins s={s} n={t.n} className="ph-margins" />}
      <ChapterEnd s={s} t={t} className="ph-chapter" />
      {looks?.map((l) => (
        <div key={l.thing} className="ph-look">
          <p className="ph-you"><span aria-hidden="true">&gt; </span>x {l.thing}</p>
          <p className="ph-prose">{l.description}</p>
          <p className="ph-sys"># inside — x one to look closer:</p>
          <ul className="ph-tree" aria-label={`Inside ${l.thing}`}>
            {l.children.map((ch, i) => (
              <li key={ch.id}>
                <span className="ph-branch" aria-hidden="true">{i === l.children.length - 1 ? "└─ " : "├─ "}</span>
                <button type="button" className="lm-thing ph-thing" onClick={() => onLook(ch.name)}>{ch.name}</button>
                <span className="ph-dim"> {ch.hint}</span>
              </li>
            ))}
          </ul>
          <LookActions s={s} thing={l.thing} className="ph-sys" />
        </div>
      ))}
    </article>
  );
}

function panel(p: Panel, view: StoryView, now: number, s?: StoryUI): ReactNode {
  if (p === "map") {
    return (
      <section key="map">
        <h2>here · where you are</h2>
        <WhereYouAre view={view} onLook={(name) => s?.look(now, name)} title="you are here" className="ph-here-map" />
      </section>
    );
  }
  if (p === "items") {
    const items = view.things.filter((t) => t.kind === "thing").sort((a, b) => Math.max(...b.seen) - Math.max(...a.seen)).slice(0, 6);
    return (
      <section key="items">
        <h2>found · {view.things.filter((t) => t.kind === "thing").length} things</h2>
        {items.length ? (
          <div className="ph-cards">
            {items.map((t) => <pre key={t.id} className="ph-pre ph-card">{asciiCard(t.name, t.hint, 22)}</pre>)}
          </div>
        ) : <p className="ph-dim">(nothing found yet)</p>}
      </section>
    );
  }
  const threads = threadOrder(view.threads);
  return (
    <section key="threads">
      <h2>threads · {view.threads.filter((t) => t.state === "open").length} open</h2>
      {threads.length ? (
        <ul className="ph-threads">
          {threads.map((t) => {
            const quiet = quietFor(t, now);
            return (
              <li key={t.id} className={t.state === "closed" ? "ph-closed" : quiet > 3 ? "ph-quiet" : ""} title={t.hint}>
                {t.state === "open" && s?.id
                  ? <button type="button" className="ph-link lm-pull" data-fray={frayOf(t, now)} onClick={() => s.pull(t)} disabled={s.busy} title={`pull ${t.name}`}>{quiet > 3 ? "○" : "●"} {t.name}</button>
                  : <>{t.state === "closed" ? "✓" : quiet > 3 ? "○" : "●"} {t.name}</>}
                <span className="ph-dim"> · {t.state === "closed" ? "closed" : quiet > 9 ? `fraying · quiet ${quiet} turns` : quiet > 3 ? `quiet ${quiet} turns` : "live"}{t.state === "open" && s?.id ? " · tap to bring back" : ""}</span>
              </li>
            );
          })}
        </ul>
      ) : <p className="ph-dim">(no open questions yet)</p>}
    </section>
  );
}
